<template>
  <SecondaryCard>
    <div class="inputs-container">
      <div class="left-side">
        <h4 class="section-header">Schedule</h4>
        <Select
          v-model="value.emailFrequency"
          label="Email Frequency"
          name="email-frequency"
          class="dropdown-input"
          :options="emailFrequency.options"
        ></Select>
        <TextInput
          v-model="value.aggregateDays"
          class="search-input"
          type='number'
          name="report-name"
          label="Aggregate Days of Data"
          min="1"
          :required="!value.aggregateDays"
          @input="validateAggregateDaysInput"
        ></TextInput>
      </div>
      <div class="right-side">
        <CalendarPickerV02
          v-model="value.startDate"
          class="calendar-picker"
          label="Start Date"
          :min="addDaysToDate(new Date(), 1)"
          :displayFormat="dateInputDisplayFormat"
          :displayRegExp="dateInputDisplayRegex"
          :key="getDateInMMDDYYY(value.startDate)"
          :allowed-dates="allowedDates"
        ></CalendarPickerV02>
        <CalendarPickerV02
          v-model="value.endDate"
          class="calendar-picker"
          label="End Date"
          :min="addDaysToDate(new Date(), 2)"
          :displayFormat="dateInputDisplayFormat"
          :displayRegExp="dateInputDisplayRegex"
          :key="getDateInMMDDYYY(value.endDate)"
          :allowed-dates="allowedDates"
        ></CalendarPickerV02>
      </div>
    </div>
    <div class='file-type'>
      <h4 class="section-header">File Type</h4>
      <Radio v-for="option in fileType.options"
        :key="option.label"
        :value="option.value"
        class="radio-button"
        name="file-type-options"
        v-model="value.fileType"
      >
        {{ option.label }}
      </Radio>
    </div>
    <label
      class="schedule-summary-label"
      :class="{ error: displayErrorMessage }"
      v-html="infoMessageText"
    />
    <label
      class="schedule-summary-label"
      v-html="dateWindowInfoText"
    />
  </SecondaryCard>
</template>

<script>
import {
  SecondaryCard,
  Select,
  Radio,
  CalendarPickerV02,
  TextInput,
} from '@mikmak/components';
import {
  format as formatDate,
  addDays,
  addMonths,
  isAfter,
} from 'date-fns';
import { getFirstDayOfDelivery }
  from '../../../../services/dashboard/scheduledReportService';

export default {
  components: {
    SecondaryCard,
    Select,
    Radio,
    CalendarPickerV02,
    TextInput,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    isNewReport: {
      type: Boolean,
      default: false,
    },
    reportDataWindowStart: {
      type: String,
    },
    reportDataWindowEnd: {
      type: String,
    },
  },
  data() {
    return {
      emailFrequency: {
        options: [
          { key: 'Daily', value: 'daily' },
          { key: 'Weekly', value: 'weekly' },
          { key: 'Bi-Weekly', value: 'biweekly' },
          { key: 'Monthly', value: 'monthly' },
          { key: 'Quarterly (Jan, Apr, July, Oct)', value: 'quarterly' },
        ],
      },
      fileType: {
        options: [
          { label: 'CSV (comma separated value)', value: 'csv' },
          { label: 'XLSX (Microsoft Excel)', value: 'xlsx' },
        ],
      },
      dateInputDisplayFormat: 'MM-dd-yyyy',
      dateInputDisplayRegex: /^\d{1,2}([./-])\d{1,2}\1\d{4}/,
    };
  },
  methods: {
    getOneMonthFromDate(date) {
      return addMonths(date, 1);
    },
    getDateInMMDDYYY(date) {
      return formatDate(date, 'MM/dd/yyyy');
    },
    addDaysToDate(date, days) {
      return addDays(date, days);
    },
    validateAggregateDaysInput(val) {
      if (val.includes('-')) {
        this.value.aggregateDays = val.replace('-', '');
      }
    },
  },
  computed: {
    dateWindowInfoText() {
      return `
        The emailed report will contain the last <b>${this.value.aggregateDays || 0}</b>
        days of data from when the report is received.
      `;
    },
    displayErrorMessage() {
      const firstDateOfDelivery =
        getFirstDayOfDelivery(this.value.emailFrequency, this.value.startDate);

      return this.value.endDate < this.value.startDate
        || this.value.endDate < firstDateOfDelivery;
    },
    infoMessageText() {
      const firstDateOfDelivery =
        getFirstDayOfDelivery(this.value.emailFrequency, this.value.startDate);

      if (!this.value.emailFrequency) {
        return `Please select email frequency.`;
      } else if (this.value.endDate < this.value.startDate) {
        return `Start date must be before end date.`;
      } else if (isAfter(firstDateOfDelivery, this.value.endDate)) {
        return `
          End Date must be after first date of delivery,
            <b>${this.getDateInMMDDYYY(firstDateOfDelivery)}.</b>
        `;
      } else {
        return `
          Your report will send <b>${this.value.emailFrequency}</b>
          starting <b>${this.getDateInMMDDYYY(firstDateOfDelivery)}</b>
          to <b>${this.getDateInMMDDYYY(this.value.endDate)}</b>.
        `;
      }
    },
    allowedDates() {
      const { emailFrequency = '' } = this.value;
      const schedules = {
        daily: () => true,
        weekly: val => new Date(val).getDay() === 0,
        biweekly: val => [1, 14].includes(new Date(`${val} 9:00`).getDate()),
        monthly: val => new Date(`${val} 9:00`).getDate() === 1,
        quarterly: val => {
          const month = new Date(`${val} 9:00`).getMonth();
          const day = new Date(`${val} 9:00`).getDate();

          return month % 3 === 0 && day === 1;
        },
      };

      return schedules[emailFrequency] || schedules.daily;
    },
  },
};
</script>

<style scoped>
.section-header {
  margin: 0;
  color: #223448;
  margin-bottom: 20px;
}

.left-side,
.right-side {
  display: inline-block;
}

.right-side {
  float: right;
  margin-right: 15px;
  margin-top: 39.5px;
}

.dropdown-input,
.date-input {
  display: block;
  margin: 12px 0;
  width: 255px;
}

.checkbox {
  margin: 15px 0 10px 0;
  cursor: pointer;
}

.schedule-summary-label {
  display: block;
  font-size: 16px;
  margin-top: 16px;
  color: #223448;
}
.schedule-summary-label.error {
  color: #CB4444;
}

.radio-button {
  margin: 12px 0;
  cursor: pointer;
}

.date-input .wrapper {
  display: none;
  border: solid
}

.calendar-picker {
  overflow: visible;
  width: 255px;
  margin-bottom: 4px;
}

.date-input {
  border: solid red;
}

.file-type {
margin-top: 20px;
margin-bottom: 20px;
}

.inputs-container {
  display: flex;
  justify-content: space-between;
}
</style>
